/**
 * Tiny CSV exporter — no dependencies. Handles RFC 4180 quoting:
 *   - Wrap any cell containing comma, quote, newline, or carriage return.
 *   - Escape internal quotes by doubling them.
 *
 * Numbers are written verbatim (Intl-formatted strings keep their grouping
 * separators and currency symbols, which Excel can still parse but accountants
 * usually want raw numbers — so callers should pass the raw number, not the
 * formatted string, for amount columns).
 */

export type CsvCell = string | number | boolean | null | undefined;
export type CsvRow = CsvCell[];

function escapeCell(value: CsvCell): string {
  if (value === null || value === undefined) return '';
  const s = typeof value === 'string' ? value : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function toCsv(rows: CsvRow[]): string {
  return rows.map((r) => r.map(escapeCell).join(',')).join('\r\n');
}

/** Trigger a browser download for the given CSV string. */
export function downloadCsv(filename: string, csv: string): void {
  // Add UTF-8 BOM so Excel opens with correct encoding for ZAR symbol etc.
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Convenience: build CSV with header + data rows then download. */
export function exportRowsAsCsv(filename: string, header: string[], dataRows: CsvRow[]): void {
  downloadCsv(filename, toCsv([header, ...dataRows]));
}

/** Date stamp suitable for CSV filenames: 2026-05-04. */
export function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}
